import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, MenuItem, Select, TextField, Typography } from '@mui/material';
import { Control, Controller, FieldErrors, UseFormRegister } from 'react-hook-form';
import { Lead } from '../../services/leadService';
import { LeadFieldConfig, LeadStatusConfig } from '../../services/leadConfigService';
import { ClinicDialog } from './ClinicDialog';
import { CLINIC_DIALOG_SX, POPUP_COLORS } from '../../constants/popupStyles';
import { DynamicField, groupConfigsIntoTabs, passesDisplayCondition } from './dynamicFields';

/**
 * Add or edit a lead.
 *
 * The built-in half is short on purpose - a lead is a name and a way to reach
 * them. Everything else a tenant wants is a lead_field_configs row, rendered
 * by the same machinery the patient dialog uses, so the two cannot drift.
 *
 * The district is shown but never editable: it is the HMO access boundary,
 * derived from whoever opened the referral, and the API refuses to change it.
 */

interface LeadFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  editingLead?: Lead | null;
  register: UseFormRegister<any>;
  errors: FieldErrors;
  /** react-hook-form formState.submitCount - used to jump to the errored tab on failed save. */
  submitCount?: number;
  control: Control<any>;
  fieldConfigs: LeadFieldConfig[];
  statusConfigs: LeadStatusConfig[];
  /** Whatever the save failed on, shown inside the dialog. It belongs here and
   *  not on the page behind: the dialog stays open on failure, so a banner
   *  rendered on the list is covered by the very form the person has to fix. */
  submitError?: string | null;
  /** The status currently chosen in the form, so display_condition can react to it. */
  watchStatus?: string;
}

const OVERVIEW = 'overview';
const STATIC_TAB_LABELS: Record<string, string> = { 'Overview': OVERVIEW };

export const LeadFormDialog: React.FC<LeadFormDialogProps> = ({
  open,
  onClose,
  onSubmit,
  editingLead,
  register,
  errors,
  submitCount = 0,
  control,
  fieldConfigs,
  statusConfigs,
  submitError,
  watchStatus,
}) => {
  const [activeTab, setActiveTab] = useState<string>(OVERVIEW);

  const editConfigs = useMemo(
    () => fieldConfigs.filter(c => c.display_in_edit),
    [fieldConfigs],
  );

  // A field with no tab name still has to appear somewhere, or configuring one
  // and never seeing it is the result. Overview is where it goes.
  const { staticExtensions, dynamicTabs } = useMemo(
    () => groupConfigsIntoTabs(
      editConfigs.map(c => ({ ...c, edit_tab_name: c.edit_tab_name || 'Overview' })),
      STATIC_TAB_LABELS,
    ),
    [editConfigs],
  );

  useEffect(() => {
    if (open) setActiveTab(OVERVIEW);
  }, [open]);

  const fieldKeyToTab = useMemo(() => {
    const m: Record<string, string> = {};
    dynamicTabs.forEach(t => t.configs.forEach(c => { m[c.key] = t.name; }));
    staticExtensions.forEach((cfgs, staticKey) => cfgs.forEach(c => { m[c.key] = staticKey; }));
    return m;
  }, [dynamicTabs, staticExtensions]);

  // On each failed save, jump to the first tab holding an error - otherwise a
  // required field on a hidden tab blocks the save with nothing on screen to
  // explain why. Keyed off submitCount because react-hook-form's `errors`
  // object reference can be stable across submits.
  useEffect(() => {
    if (!submitCount) return;
    const tabs = new Set<string>();
    Object.keys(errors || {}).forEach((key) => {
      if (key === 'additional_fields') {
        const afErrors = (errors.additional_fields as Record<string, unknown>) || {};
        Object.keys(afErrors).forEach((afKey) => tabs.add(fieldKeyToTab[afKey] || OVERVIEW));
      } else {
        tabs.add(OVERVIEW);
      }
    });
    if (tabs.size === 0 || tabs.has(activeTab)) return;
    const target = tabs.has(OVERVIEW)
      ? OVERVIEW
      : dynamicTabs.map(t => t.name).find(n => tabs.has(n)) || Array.from(tabs)[0];
    if (target) setActiveTab(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitCount]);

  const tabStyle = (tab: string) => ({
    px: 2,
    py: 1,
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: activeTab === tab ? 600 : 400,
    color: activeTab === tab ? POPUP_COLORS.titleText : '#888',
    background: 'none',
    border: 'none',
    borderBottomStyle: 'solid',
    borderBottomWidth: '2px',
    borderBottomColor: activeTab === tab ? POPUP_COLORS.titleText : 'transparent',
    transition: 'all 0.15s',
    marginRight: '16px',
    paddingLeft: 0,
    paddingRight: 0,
  } as React.CSSProperties);

  const renderDynamic = (config: LeadFieldConfig) => {
    if (!passesDisplayCondition(config, watchStatus)) return null;
    return (
      <DynamicField
        key={`${config.key}-${editingLead?.id ?? 'new'}`}
        config={config}
        register={register}
        control={control}
        defaultValue={editingLead?.additional_fields?.[config.key]}
        errors={errors}
      />
    );
  };

  const textInputProps = { style: CLINIC_DIALOG_SX.textField as React.CSSProperties };
  const err = (name: string) => (errors?.[name] as any)?.message as string | undefined;

  const actions = (
    <>
      <Button type="submit" variant="contained" sx={CLINIC_DIALOG_SX.primaryButton}>
        {editingLead ? 'Update' : 'Add'}
      </Button>
      <Button onClick={onClose} sx={CLINIC_DIALOG_SX.cancelButton}>
        Cancel
      </Button>
    </>
  );

  return (
    <ClinicDialog
      open={open}
      onClose={onClose}
      title={editingLead ? 'Edit Lead' : 'Add New Lead'}
      onSubmit={onSubmit}
      actions={actions}
    >
      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}

      <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(54,106,117,0.15)', mb: 2, gap: 0 }}>
        <button type="button" onClick={() => setActiveTab(OVERVIEW)} style={tabStyle(OVERVIEW)}>
          Overview
        </button>
        {dynamicTabs.map((dt) => (
          <button key={dt.name} type="button" onClick={() => setActiveTab(dt.name)} style={tabStyle(dt.name)}>
            {dt.name}
          </button>
        ))}
      </Box>

      <Box sx={{ display: 'grid' }}>
        {/* Every tab is rendered and toggled with visibility rather than
            unmounted, so react-hook-form keeps the values on tabs nobody is
            looking at. */}
        <Box sx={{ gridArea: '1/1', display: 'flex', flexDirection: 'column', gap: 2, visibility: activeTab === OVERVIEW ? 'visible' : 'hidden' }}>
          <Box sx={CLINIC_DIALOG_SX.fieldRow}>
            <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>First Name</Typography>
            <TextField
              {...register('first_name', { required: 'First name is required' })}
              fullWidth size="small" error={!!err('first_name')} helperText={err('first_name')}
              InputProps={textInputProps}
            />
          </Box>

          <Box sx={CLINIC_DIALOG_SX.fieldRow}>
            <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Last Name</Typography>
            <TextField
              {...register('last_name', { required: 'Last name is required' })}
              fullWidth size="small" error={!!err('last_name')} helperText={err('last_name')}
              InputProps={textInputProps}
            />
          </Box>

          <Box sx={CLINIC_DIALOG_SX.fieldRow}>
            <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Email</Typography>
            <TextField
              {...register('email')}
              fullWidth size="small" type="email" InputProps={textInputProps}
            />
          </Box>

          <Box sx={CLINIC_DIALOG_SX.fieldRow}>
            <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Phone</Typography>
            <TextField {...register('phone')} fullWidth size="small" InputProps={textInputProps} />
          </Box>

          <Box sx={CLINIC_DIALOG_SX.fieldRow}>
            <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>ID Number</Typography>
            <TextField {...register('id_number')} fullWidth size="small" InputProps={textInputProps} />
          </Box>

          <Box sx={CLINIC_DIALOG_SX.fieldRow}>
            <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Status</Typography>
            <Controller
              name="status"
              control={control}
              defaultValue={editingLead?.status ?? ''}
              render={({ field }) => (
                <Select {...field} value={field.value ?? ''} fullWidth size="small" displayEmpty sx={CLINIC_DIALOG_SX.select}>
                  <MenuItem value=""><em>Default</em></MenuItem>
                  {statusConfigs.map((sc) => (
                    <MenuItem key={sc.key} value={sc.key}>{sc.title}</MenuItem>
                  ))}
                </Select>
              )}
            />
          </Box>

          {/* Read-only: the district is the access boundary, derived from
              whoever opened the referral. The API refuses to change it. */}
          {editingLead?.sub_clinic && (
            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>District</Typography>
              <Typography variant="body2" sx={{ flex: 1, color: '#666' }}>{editingLead.sub_clinic}</Typography>
            </Box>
          )}

          {(staticExtensions.get(OVERVIEW) ?? []).map(renderDynamic)}
        </Box>

        {dynamicTabs.map((dt) => (
          <Box
            key={dt.name}
            sx={{ gridArea: '1/1', display: 'flex', flexDirection: 'column', gap: 2, visibility: activeTab === dt.name ? 'visible' : 'hidden' }}
          >
            {dt.configs.map(renderDynamic)}
          </Box>
        ))}
      </Box>
    </ClinicDialog>
  );
};

export default LeadFormDialog;
